import classNames from 'classnames';
import React, { ChangeEvent, useState } from 'react';

import { LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { Icon, IconTypes } from '@eika/icon';
import { ErrorLabel, WarningLabel } from '@eika/label';

import { useUpdateAccountAlias } from '../../fetchHooks/useAccount';
import type { Account } from '../../generated-types';
import useTranslation, { format } from '../../hooks/useTranslation';

import './AccountNameBox.scss';

interface Props extends React.PropsWithChildren {
    account: Account;
}

const maxCharacters = 34;

const AccountNameBox: React.FC<Props> = ({ account, children }) => {
    const [accountName, setAccountName] = useState(account.alias);
    const [showExceededMaxCharactersError, setShowExceededMaxCharactersError] = useState(false);
    const [hasExceededMaxCharacters, setHasExceededMaxCharacters] = useState(false);
    const [value, setValue] = useState(account.alias);
    const [isFocused, setIsFocused] = useState(false);
    const translated = useTranslation();

    const mutateAlias = useUpdateAccountAlias(account.accountId);

    const saveAlias = (event: { preventDefault: () => void }) => {
        event.preventDefault();
        if (hasExceededMaxCharacters) {
            setShowExceededMaxCharactersError(true);
            return;
        }
        if (value === accountName) return;
        mutateAlias.mutateAsync(value).then(() => {
            setIsFocused(false);
            setAccountName(value);
        });
    };

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        mutateAlias.reset();
        setValue(value);
        setHasExceededMaxCharacters(value.length > maxCharacters);
        setShowExceededMaxCharactersError(false);
    };

    return (
        <>
            <form onSubmit={saveAlias}>
                <section
                    className={classNames(
                        'account-name-box__container',
                        { 'has-focus': isFocused },
                        { 'has-changed': value !== accountName },
                    )}
                >
                    <label className="account-name-box__input-wrapper">
                        <span className="account-name-box__label">{translated.accountDetails.accountName}</span>
                        <input
                            className="account-name-box__account_name"
                            value={value}
                            onChange={onChange}
                            disabled={mutateAlias.isPending}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                            maxLength={maxCharacters + 1}
                        />
                        <span className="account-name-box__icon">
                            <Icon type={IconTypes.PENN_24} />
                        </span>
                    </label>
                    <div className="account-name-box__action-button-wrapper">
                        {(value !== accountName || isFocused) && (
                            <>
                                <LinkButton
                                    onClick={() => setValue(accountName)}
                                    disabled={mutateAlias.isPending}
                                    onFocus={() => setIsFocused(true)}
                                    onBlur={() => setIsFocused(false)}
                                >
                                    {translated.cancel}
                                </LinkButton>
                                <ButtonWithSpinner
                                    type="submit"
                                    buttonType="positive"
                                    size="small"
                                    onClick={saveAlias}
                                    showSpinner={mutateAlias.isPending}
                                    onFocus={() => setIsFocused(true)}
                                    onBlur={() => setIsFocused(false)}
                                >
                                    {translated.save}
                                </ButtonWithSpinner>
                            </>
                        )}
                    </div>
                </section>
            </form>
            {hasExceededMaxCharacters && !showExceededMaxCharactersError && (
                <div className="account-name-box__error-label">
                    <WarningLabel text={format(translated.accountDetails.maxCharacters, maxCharacters)} />
                </div>
            )}
            {showExceededMaxCharactersError && (
                <div className="account-name-box__error-label">
                    <ErrorLabel
                        text={format(translated.accountDetails.maxCharacters, maxCharacters)}
                        icon={IconTypes.UTROPSTEGN_24}
                    />
                </div>
            )}
            {mutateAlias.isError && (
                <div className="account-name-box__error-label" data-testid="change-account-name-error">
                    <ErrorLabel text={translated.accountDetails.couldNotSave} icon={IconTypes.UTROPSTEGN_24} />
                </div>
            )}
            {children}
        </>
    );
};

export default AccountNameBox;
